import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import Utils from 'src/app/utils';

import { BREEDS } from '../../models/breeds';
import { COUNTRIES, Country } from '../../models/countries';
import {
  ArrivalAllocation,
  ArrivalAllocationTableRow,
  Arrival,
  Breed,
  Gender,
  Order,
  PropagationCompany,
  Section,
  Hatchery,
} from '../../models/cycle.model';
import { setSelectedArrivalId } from '../../store/actions/cycle.actions';
import {
  getHatcheries,
  getPropagationCompanies,
  getSections,
  getSelectedArrival,
  getSelectedCycle,
} from '../../store/selectors/cycle.selectors';

import { arrivalDateValidator } from '../../validators/arrival-date-validator';

export interface ArrivalItemDialogInputData extends AbstractFormDialogData {
  arrivalId?: number;
}
export interface ArrivalItemDialogResultData {
  arrival: Arrival;
}

@Component({
  selector: 'app-arrival-item-dialog',
  templateUrl: './arrival-item-dialog.component.html',
  styleUrls: ['./arrival-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    AgrNumberDirective,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ArrivalItemDialogComponent
  extends AbstractDialog<ArrivalItemDialogResultData>
  implements OnInit
{
  id: number;

  readonly formGroup = new FormGroup(
    {
      arrivalDate: new FormControl<string>('', [Validators.required]),
      chickBirthDate: new FormControl<string>('', [Validators.required]),
      hatchery: new FormControl<Hatchery>(undefined, [Validators.required]),
      gpsNumber: new FormControl<string>('', [Validators.required]),
      gpsBirthDate: new FormControl<string>('', [Validators.required]),
      gpsCountry: new FormControl<Country>(undefined, [Validators.required]),
      breed: new FormControl<Breed>(undefined, [Validators.required]),
      gender: new FormControl<Gender>(undefined, [Validators.required]),
      numberOfAnimals: new FormControl<number>(undefined, [Validators.required]),
    },
    { validators: arrivalDateValidator },
  );
  readonly numberOfAnimalsArray = new FormArray([]);

  readonly breeds: Breed[] = BREEDS;
  readonly genders: Gender[] = [Gender.Female, Gender.Male];
  readonly countries = COUNTRIES;
  readonly hatcheries$ = this.store.select(getHatcheries);
  readonly propagationCompanies$ = this.store.select(getPropagationCompanies);
  readonly orders$ = this.store.select(getSelectedCycle).pipe(map((c) => c.orders));

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: false,
    wrapperWidth: '100%',
  };
  allocationsTableConfig: TableConfig;
  allocationsTableColumns: TableColumn[] = [];
  allocations: ArrivalAllocationTableRow[] = [];
  isValidSumOfAllocationsAmounts = true;
  isValidCumulativeSectionSurfaceAreas: { barn: string; sections: string[] } = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ArrivalItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      ArrivalItemDialogComponent,
      AbstractDialogResultData<ArrivalItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.id = this.data.arrivalId;
  }

  ngOnInit() {
    if (this.id > 0) {
      this.data.title = this.localeService.translate('arrival.edit-arrival');

      this.store.dispatch(setSelectedArrivalId({ id: this.id }));
      this.store
        .select(getSelectedArrival)
        .pipe(
          withLatestFrom(this.hatcheries$),
          skipWhile(
            ([arrival, hatcheries]) =>
              arrival === null || arrival === undefined || hatcheries?.length === 0,
          ),
          take(1),
        )
        .subscribe(([arrival, hatcheries]) => {
          this.formGroup.controls.arrivalDate.setValue(arrival.arrivalDate);
          this.formGroup.controls.chickBirthDate.setValue(arrival.chickBirthDate);
          this.formGroup.controls.hatchery.setValue(
            hatcheries.find((h) => h.id === arrival.hatchery.id),
          );
          this.formGroup.controls.gpsNumber.setValue(arrival.gpsNumber);
          this.formGroup.controls.gpsBirthDate.setValue(arrival.gpsBirthDate);
          this.formGroup.controls.gpsCountry.setValue(
            this.countries.find((c) => c.id === arrival.gpsCountry),
          );
          this.formGroup.controls.breed.setValue(
            this.breeds.find((b) => b.id === arrival.breed.id),
          );
          this.formGroup.controls.gender.setValue(arrival.gender);
          this.formGroup.controls.numberOfAnimals.setValue(arrival.numberOfAnimals);

          this.initAllocationsTable();
          this.setAllocationsTableDatasource(
            this.formGroup.controls.breed.value,
            this.formGroup.controls.gender.value,
            arrival.allocations,
          );
        });
    } else {
      this.data.title = this.localeService.translate('arrival.add-arrival');
      this.initAllocationsTable();
    }

    this.formGroup.controls.breed.valueChanges.subscribe((value) =>
      this.setAllocationsTableDatasource(value, this.formGroup.controls.gender.value),
    );
    this.formGroup.controls.gender.valueChanges.subscribe((value) =>
      this.setAllocationsTableDatasource(this.formGroup.controls.breed.value, value),
    );
  }

  private initAllocationsTable() {
    this.allocationsTableColumns = [
      {
        id: 'sectionName',
        type: TableColumnTypes.STRING,
        label: this.localeService.translate('arrival.allocations-table.section'),
      },
      {
        id: 'propagationCompanyName',
        type: TableColumnTypes.STRING,
        label: this.localeService.translate('arrival.allocations-table.propagation-company'),
      },
      {
        id: 'numberOfAnimals',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('arrival.allocations-table.amount-of-chicks'),
        inputConfig: {
          formArray: <FormArray>this.numberOfAnimalsArray,
          step: 1,
        },
      },
    ];
    this.allocationsTableConfig = {
      ...this.tableConfigBase,
      columns: this.allocationsTableColumns,
      dataSource: new MatTableDataSource(this.allocations),
    };
  }

  private setAllocationsTableDatasource(
    breed: Breed,
    gender: Gender,
    allocations?: ArrivalAllocation[],
  ) {
    this.allocations = [];
    if (!!breed && !!gender) {
      this.store
        .select(getSections)
        .pipe(withLatestFrom(this.propagationCompanies$, this.orders$), take(1))
        .subscribe(
          ([sections, propagationCompanies, orders]: [
            Section[],
            PropagationCompany[],
            Order[],
          ]) => {
            let index = 0;
            sections
              .filter((s) => s.gender === gender && s.breed.id === breed.id)
              .map((section) => {
                propagationCompanies
                  .filter((p) =>
                    orders.some(
                      (o) =>
                        o.propagationCompany.id === p.id &&
                        o.breed.id === breed.id &&
                        ((o.numberOfFemales > 0 && gender === Gender.Female) ||
                          (o.numberOfMales > 0 && gender === Gender.Male)),
                    ),
                  )
                  .forEach((company) => {
                    this.allocations.push({
                      id: index,
                      sectionId: section.id,
                      sectionName: `${section.name} (${section.barn.name})`,
                      propagationCompanyId: company.id,
                      propagationCompanyName: company.name,
                      numberOfAnimals: allocations
                        ? (allocations.find(
                            (a) =>
                              a.section.id === section.id && a.propagationCompany.id === company.id,
                          )?.numberOfAnimals ?? null)
                        : null,
                    });
                    index++;
                  });
              });
            this.allocationsTableConfig.dataSource.data = this.allocations;
          },
        );
    }
  }

  private validateSumOfAllocationAmounts() {
    this.isValidSumOfAllocationsAmounts =
      this.numberOfAnimalsArray.value
        .filter((val: number) => !!val && val > 0)
        .reduce((acc: number, curr: number) => acc + curr, 0) ===
      this.formGroup.controls.numberOfAnimals.value;
    // update table datasource with validated values from array
    if (this.isValidSumOfAllocationsAmounts) {
      let tableRows = <ArrivalAllocationTableRow[]>(
        this.allocationsTableConfig.dataSource.data.map((row: ArrivalAllocationTableRow) =>
          Object.assign({}, row),
        )
      );
      tableRows = tableRows.map((row) => ({
        ...row,
        numberOfAnimals: this.numberOfAnimalsArray.controls[row.id].value,
      }));
      this.allocationsTableConfig.dataSource.data = tableRows;
    }
    return this.isValidSumOfAllocationsAmounts;
  }

  save() {
    if (this.formGroup.valid && this.validateSumOfAllocationAmounts()) {
      this.store
        .select(getSections)
        .pipe(withLatestFrom(this.propagationCompanies$), take(1))
        .subscribe(([sections, propagationCompanies]: [Section[], PropagationCompany[]]) => {
          const arrival: Arrival = {
            id: this.id,
            arrivalDate:
              typeof (<unknown>this.formGroup.controls.arrivalDate.value) !== 'string'
                ? Utils.toUtcDate(new Date(this.formGroup.controls.arrivalDate.value)).toISOString()
                : <string>this.formGroup.controls.arrivalDate.value,
            chickBirthDate:
              typeof (<unknown>this.formGroup.controls.chickBirthDate.value) !== 'string'
                ? Utils.toUtcDate(
                    new Date(this.formGroup.controls.chickBirthDate.value),
                  ).toISOString()
                : <string>this.formGroup.controls.chickBirthDate.value,
            hatchery: this.formGroup.controls.hatchery.value,
            gpsNumber: this.formGroup.controls.gpsNumber.value,
            gpsBirthDate:
              typeof (<unknown>this.formGroup.controls.gpsBirthDate.value) !== 'string'
                ? Utils.toUtcDate(
                    new Date(this.formGroup.controls.gpsBirthDate.value),
                  ).toISOString()
                : <string>this.formGroup.controls.gpsBirthDate.value,
            gpsCountry: this.formGroup.controls.gpsCountry.value.id,
            breed: this.formGroup.controls.breed.value,
            gender: this.formGroup.controls.gender.value,
            numberOfAnimals: this.formGroup.controls.numberOfAnimals.value,
            allocations: this.allocationsTableConfig.dataSource.data
              .filter(
                (row: ArrivalAllocationTableRow) =>
                  !!row.numberOfAnimals && row.numberOfAnimals > 0,
              )
              .map((row: ArrivalAllocationTableRow) => ({
                id: row.id,
                section: sections.find((s) => s.id === row.sectionId),
                numberOfAnimals: row.numberOfAnimals,
                propagationCompany: propagationCompanies.find(
                  (s) => s.id === row.propagationCompanyId,
                ),
              })),
          };
          this.confirm({ arrival });
        });
    }
  }
}
